import { BoxProps, Flex, Text } from '@chakra-ui/react'
import { useScreen } from '@hb/react/src/hooks/useScreen'
import { FieldMapValue, isInfoStage, OnUploadProgress } from '@hb/shared'
import { FORM_ERROR, FormApi } from 'final-form'
import { useCallback, useContext, useMemo } from 'react'
import { SolidActionButton } from '../../Buttons/ActionButton'
import { GenericEditModal } from '../../DataView/GenericEditModal'
import { DefaultModal } from '../../Modals'
import { DBTextViewModal } from '../DBTextStage'
import { RenderStageComponent, RenderStageProps } from '../renderStage/types'
import { FormWizardContext } from './context'
import { useStageState } from './hooks'

const DefaultReadOnlyFooter = () => (
  <Flex w="100%" bg="gray.50" pt={2}>
    <Text fontWeight={500} fontFamily="Open Sans">
      This information cannot be edited after submission
    </Text>
  </Flex>
)

const CustomRenderStageModal = ({
  RenderStage,
  isOpen,
  onClose,
  ...props
}: RenderStageProps & {
  RenderStage: RenderStageComponent
  isOpen: boolean
  onClose: () => void
}) => {
  const { height: screenHeight } = useScreen()
  const modalHeight = useMemo(() => Math.floor(Math.min(screenHeight * 0.85, 800)), [screenHeight])
  return (
    <DefaultModal
      isOpen={isOpen}
      onClose={onClose}
      size="3xl"
      contentProps={{
        height: `${modalHeight}px`,
        backgroundColor: 'transparent',
        boxShadow: 'none',
      }}
      bodyProps={{
        // height: `${modalHeight}px`,
        boxShadow: 'md',
      }}
      overlayHeader
      isCentered={false}
      closeOnOverlayClick={false}
      render={() => <RenderStage {...props} />}
    />
  )
}

const formBoxProps: BoxProps = {
  bg: 'transparent',
  boxShadow: 'none',
  p: 0,
}

export const StageModal = () => {
  const {
    close,
    stageId,
    stage,
    onSubmitStage,
    data: formData,
    corrections: formCorrections,
    form,
    storagePath,
    nextStage,
    adminView,
    assessmentId,
    patientId,
    ReadOnlyFooter,
    RenderStage,
    readOnly,
    inputSize,
  } = useContext(FormWizardContext)

  const { value: data, corrections } = useStageState(
    form,
    stage,
    formData,
    formCorrections,
    stageId,
  )

  const handleSubmit = useCallback(
    async (submitted: FieldMapValue, onUploadProgress: OnUploadProgress, formApi?: FormApi) => {
      try {
        const res = await onSubmitStage(submitted, onUploadProgress, formApi) //, !adminView)
        if (res?.error) return { error: res.error }
        if (res?.[FORM_ERROR]) return { error: res[FORM_ERROR] }
      } catch (e: any) {
        console.error(e)
        return { error: e?.message || 'An error occurred' }
      }
      return { success: 'Saved successfully' }
    },
    [onSubmitStage],
  )
  const ReadOnlyFooterComponent = ReadOnlyFooter ?? DefaultReadOnlyFooter
  const stageIsInfo = useMemo(() => stage && isInfoStage(stage), [stage])
  if (RenderStage && !adminView)
    return (
      <CustomRenderStageModal
        RenderStage={RenderStage}
        isOpen={!!stage}
        onComplete={close}
        onClose={close}
        assessmentId={assessmentId}
        patientId={patientId}
        data={data}
        onSubmit={onSubmitStage}
        readOnly={readOnly}
        storagePath={storagePath}
      />
    )
  return stageIsInfo ? (
    <DBTextViewModal
      isCentered={false}
      isOpen={!!stage}
      onClose={close}
      value={stage && isInfoStage(stage) ? stage.data : []}
      footerProps={{ p: 2, bg: '#eeeeee', borderTop: '1px solid #cdcdcd' }}
      renderFooter={() => (
        <Flex>
          <SolidActionButton onClick={() => onSubmitStage(undefined, () => {})}>
            Next
          </SolidActionButton>
        </Flex>
      )}
    />
  ) : (
    <GenericEditModal
      isOpen={!!stage}
      closeOnSubmit={false}
      canSubmitClean
      readOnly={readOnly}
      baseStoragePath={storagePath}
      inputSize={inputSize}
      isCentered={false}
      adminView={adminView}
      corrections={corrections}
      footerProps={{ borderTop: '1px solid #cdcdcd', p: 0 }}
      renderFooter={readOnly ? () => <ReadOnlyFooterComponent /> : undefined}
      onClose={close}
      onSubmit={handleSubmit}
      field={stage && !isInfoStage(stage) ? stage : undefined}
      data={data}
      formBoxProps={formBoxProps}
      submitText={!nextStage ? 'Finish' : 'Next'}
    />
  )
}
